import React, { useState, useEffect } from 'react';
import { X, Upload, Video, Image as ImageIcon, Sparkles } from 'lucide-react';
import { BannerSlide, updateBannerSlide } from '../../services/landing/bannerSlideService';

interface EditBannerSlideModalProps {
    isOpen: boolean;
    slide: BannerSlide | null;
    onClose: () => void;
    onSuccess: () => void;
}

const COLOR_PRESETS = [
    { label: 'Magenta Co Marca', value: '#c1348b' },
    { label: 'Púrpura Imperial', value: '#61257B' },
    { label: 'Azul Corporativo', value: '#007FC8' },
    { label: 'Naranja Vibrante', value: '#ea8406' },
    { label: 'Dorado Sutil', value: '#ffd97c' },
    { label: 'Blanco Nieve', value: '#ffffff' },
];

export const EditBannerSlideModal: React.FC<EditBannerSlideModalProps> = ({
    isOpen,
    slide,
    onClose,
    onSuccess,
}) => {
    const [tipo, setTipo] = useState<'image' | 'video'>('image');
    const [titulo, setTitulo] = useState('');
    const [tituloAcento, setTituloAcento] = useState('');
    const [colorAcento, setColorAcento] = useState('#c1348b');
    const [descripcion, setDescripcion] = useState('');
    const [textoBotonPrimario, setTextoBotonPrimario] = useState('Cotizar algún servicio');
    const [textoBotonSecundario, setTextoBotonSecundario] = useState('Hablar con un asesor');
    const [enlaceBotonSecundario, setEnlaceBotonSecundario] = useState('');
    const [videoUrl, setVideoUrl] = useState('');
    const [orden, setOrden] = useState(0);
    const [activo, setActivo] = useState(true);

    const [imagenFile, setImagenFile] = useState<File | null>(null);
    const [videoFile, setVideoFile] = useState<File | null>(null);
    const [newPreviewUrl, setNewPreviewUrl] = useState<string | null>(null);
    const [newVideoPreviewUrl, setNewVideoPreviewUrl] = useState<string | null>(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen && slide) {
            setTipo(slide.tipo || 'image');
            setTitulo(slide.titulo || '');
            setTituloAcento(slide.titulo_acento || '');
            setColorAcento(slide.color_acento || '#c1348b');
            setDescripcion(slide.descripcion || '');
            setTextoBotonPrimario(slide.texto_boton_primario || 'Cotizar algún servicio');
            setTextoBotonSecundario(slide.texto_boton_secundario || 'Hablar con un asesor');
            setEnlaceBotonSecundario(slide.enlace_boton_secundario || '');
            setVideoUrl(slide.video_url || '');
            setOrden(slide.orden || 0);
            setActivo(slide.activo);
            setImagenFile(null);
            setVideoFile(null);
            setNewPreviewUrl(null);
            setNewVideoPreviewUrl(null);
            setError('');
        }
    }, [isOpen, slide]);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setImagenFile(file);
            const objectUrl = URL.createObjectURL(file);
            setNewPreviewUrl(objectUrl);
        }
    };

    const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setVideoFile(file);
            const objectUrl = URL.createObjectURL(file);
            setNewVideoPreviewUrl(objectUrl);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!slide) return;
        setError('');

        if (!titulo.trim() && !tituloAcento.trim()) {
            setError('Por favor ingresa al menos un título o texto de acento');
            return;
        }

        setLoading(true);

        try {
            const formData = new FormData();
            formData.append('titulo', titulo.trim());
            formData.append('titulo_acento', tituloAcento.trim());
            formData.append('color_acento', colorAcento);
            formData.append('descripcion', descripcion.trim());
            formData.append('tipo', tipo);
            formData.append('texto_boton_primario', textoBotonPrimario.trim() || 'Cotizar algún servicio');
            formData.append('texto_boton_secundario', textoBotonSecundario.trim() || 'Hablar con un asesor');
            formData.append('enlace_boton_secundario', enlaceBotonSecundario.trim());
            formData.append('video_url', videoUrl.trim());
            formData.append('orden', String(orden));
            formData.append('activo', String(activo));

            if (imagenFile) {
                formData.append('imagen', imagenFile);
            }
            if (videoFile) {
                formData.append('video', videoFile);
            }

            await updateBannerSlide(slide.id, formData);
            onSuccess();
            onClose();
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            setError(err.response?.data?.detail || 'Error al actualizar la diapositiva.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !slide) return null;

    const existingImageUrl = slide.url_imagen || slide.imagen;
    const existingVideoUrl = slide.url_video || slide.video || slide.video_url;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
            <div className="relative w-full max-w-2xl bg-white dark:bg-[#120a1f] rounded-2xl shadow-2xl border border-gray-200 dark:border-purple-900/40 my-8 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-purple-900/30">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center text-[#c1348b]">
                            <Sparkles size={20} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                Editar Diapositiva #{slide.orden}
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Actualiza multimedia, textos y visibilidad</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-purple-900/30 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {error && (
                    <div className="mx-6 mt-4 p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/50 rounded-xl text-red-600 dark:text-red-400 text-sm">
                        {error}
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[75vh] overflow-y-auto custom-scroll">
                    {/* Selector de Tipo */}
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-2">
                            Tipo de Medio de Fondo
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => setTipo('image')}
                                className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border font-semibold text-sm transition-all ${
                                    tipo === 'image'
                                        ? 'bg-[#c1348b]/10 border-[#c1348b] text-[#c1348b] shadow-sm'
                                        : 'border-gray-200 dark:border-purple-900/40 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-purple-900/20'
                                }`}
                            >
                                <ImageIcon size={18} />
                                <span>Fotografía / Imagen</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => setTipo('video')}
                                className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl border font-semibold text-sm transition-all ${
                                    tipo === 'video'
                                        ? 'bg-[#61257B]/10 border-[#61257B] text-[#61257B] dark:text-purple-400 shadow-sm'
                                        : 'border-gray-200 dark:border-purple-900/40 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-purple-900/20'
                                }`}
                            >
                                <Video size={18} />
                                <span>Video Cinematográfico</span>
                            </button>
                        </div>
                    </div>

                    {/* Previsualización y Reemplazo de Imagen */}
                    {tipo === 'image' && (
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Imagen de fondo
                            </label>
                            <div className="mt-1 flex flex-col items-center p-4 border-2 border-gray-300 dark:border-purple-900/50 border-dashed rounded-xl bg-gray-50/50 dark:bg-purple-950/10">
                                {newPreviewUrl ? (
                                    <div className="text-center mb-3">
                                        <img src={newPreviewUrl} alt="Nueva" className="max-h-44 rounded-lg shadow-md object-cover" />
                                        <span className="block mt-1 text-xs text-green-600 font-semibold">Nueva imagen lista para guardar</span>
                                    </div>
                                ) : existingImageUrl ? (
                                    <div className="text-center mb-3">
                                        <img src={existingImageUrl} alt="Actual" className="max-h-44 rounded-lg shadow-md object-cover" />
                                        <span className="block mt-1 text-xs text-gray-500">Imagen actual</span>
                                    </div>
                                ) : (
                                    <Upload className="mx-auto h-10 w-10 text-gray-400 mb-2" />
                                )}

                                <label className="relative cursor-pointer rounded-md font-semibold text-[#c1348b] hover:underline text-sm">
                                    <span>{existingImageUrl ? 'Reemplazar imagen...' : 'Subir imagen...'}</span>
                                    <input type="file" accept="image/*" className="sr-only" onChange={handleImageChange} />
                                </label>
                            </div>
                        </div>
                    )}

                    {/* Previsualización y Reemplazo de Video */}
                    {tipo === 'video' && (
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    Archivo de Video
                                </label>
                                <div className="mt-1 flex flex-col items-center p-4 border-2 border-gray-300 dark:border-purple-900/50 border-dashed rounded-xl bg-gray-50/50 dark:bg-purple-950/10">
                                    {newVideoPreviewUrl ? (
                                        <div className="text-center mb-3">
                                            <video src={newVideoPreviewUrl} controls className="max-h-40 rounded-lg shadow-md" />
                                            <span className="block mt-1 text-xs text-purple-600 font-semibold">Nuevo video seleccionado</span>
                                        </div>
                                    ) : existingVideoUrl ? (
                                        <div className="text-center mb-3">
                                            <video src={existingVideoUrl} controls className="max-h-40 rounded-lg shadow-md" />
                                            <span className="block mt-1 text-xs text-gray-500">Video actual</span>
                                        </div>
                                    ) : (
                                        <Video className="mx-auto h-10 w-10 text-gray-400 mb-2" />
                                    )}

                                    <label className="relative cursor-pointer rounded-md font-semibold text-[#61257B] dark:text-purple-400 hover:underline text-sm">
                                        <span>{existingVideoUrl ? 'Reemplazar archivo de video...' : 'Subir archivo de video...'}</span>
                                        <input type="file" accept="video/mp4,video/webm" className="sr-only" onChange={handleVideoChange} />
                                    </label>
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                    O enlace/URL de Video
                                </label>
                                <input
                                    type="text"
                                    value={videoUrl}
                                    onChange={(e) => setVideoUrl(e.target.value)}
                                    placeholder="/videos/sample.mp4 o URL externa"
                                    className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#c1348b]"
                                />
                            </div>
                        </div>
                    )}

                    {/* Títulos */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Título Principal
                            </label>
                            <input
                                type="text"
                                value={titulo}
                                onChange={(e) => setTitulo(e.target.value)}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#c1348b]"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Texto con Acento (Destacado)
                            </label>
                            <input
                                type="text"
                                value={tituloAcento}
                                onChange={(e) => setTituloAcento(e.target.value)}
                                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#c1348b]"
                            />
                        </div>
                    </div>

                    {/* Color de Acento */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                            Color del Texto Destacado
                        </label>
                        <div className="flex items-center gap-2 flex-wrap">
                            {COLOR_PRESETS.map((p) => (
                                <button
                                    key={p.value}
                                    type="button"
                                    onClick={() => setColorAcento(p.value)}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                                        colorAcento === p.value
                                            ? 'border-gray-900 dark:border-white shadow-sm scale-105'
                                            : 'border-gray-200 dark:border-purple-900/30'
                                    }`}
                                >
                                    <span
                                        className="w-3.5 h-3.5 rounded-full border border-black/10 inline-block"
                                        style={{ backgroundColor: p.value }}
                                    />
                                    <span className="text-gray-700 dark:text-gray-300">{p.label}</span>
                                </button>
                            ))}
                            <div className="flex items-center gap-1.5 ml-auto">
                                <input
                                    type="color"
                                    value={colorAcento}
                                    onChange={(e) => setColorAcento(e.target.value)}
                                    className="w-8 h-8 rounded-lg cursor-pointer border border-gray-300 p-0.5"
                                />
                                <span className="text-xs text-gray-500 font-mono">{colorAcento}</span>
                            </div>
                        </div>
                    </div>

                    {/* Descripción */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            Descripción / Subtítulo
                        </label>
                        <textarea
                            rows={3}
                            value={descripcion}
                            onChange={(e) => setDescripcion(e.target.value)}
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#c1348b]"
                        />
                    </div>

                    {/* Botones */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Texto Botón 1 (Cotizador)
                            </label>
                            <input
                                type="text"
                                value={textoBotonPrimario}
                                onChange={(e) => setTextoBotonPrimario(e.target.value)}
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#c1348b]"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Texto Botón 2 (Asesor WhatsApp)
                            </label>
                            <input
                                type="text"
                                value={textoBotonSecundario}
                                onChange={(e) => setTextoBotonSecundario(e.target.value)}
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#c1348b]"
                            />
                        </div>
                    </div>

                    {/* Enlace WhatsApp personalizado opcional */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            Enlace personalizado para Botón 2 (Opcional)
                        </label>
                        <input
                            type="text"
                            value={enlaceBotonSecundario}
                            onChange={(e) => setEnlaceBotonSecundario(e.target.value)}
                            placeholder="Dejar vacío para usar el WhatsApp predeterminado"
                            className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#c1348b]"
                        />
                    </div>

                    {/* Orden y Activo */}
                    <div className="grid grid-cols-2 gap-4 items-center pt-2">
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Posición / Orden numérico
                            </label>
                            <input
                                type="number"
                                min={0}
                                value={orden}
                                onChange={(e) => setOrden(parseInt(e.target.value) || 0)}
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#c1348b]"
                            />
                        </div>
                        <div className="flex items-center gap-3 pt-5">
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={activo}
                                    onChange={(e) => setActivo(e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#c1348b]"></div>
                                <span className="ml-3 text-sm font-semibold text-gray-800 dark:text-gray-200">
                                    {activo ? 'Visible en Landing' : 'Oculto / Inactivo'}
                                </span>
                            </label>
                        </div>
                    </div>

                    {/* Footer Buttons */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-purple-900/30">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="px-5 py-2.5 rounded-xl border border-gray-300 dark:border-purple-900/40 text-gray-700 dark:text-gray-300 font-semibold text-sm hover:bg-gray-50 dark:hover:bg-purple-900/20 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#c1348b] to-[#61257B] text-white font-semibold text-sm shadow-lg shadow-purple-900/25 hover:opacity-95 transition-all disabled:opacity-50"
                        >
                            {loading ? 'Guardando...' : 'Guardar Cambios'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditBannerSlideModal;
